import React from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours } from "@/utils/themeUtils";
import { filterStudentClaimsForOtherClasses, useStudentClaimsQuery } from "@/query/studentClaimsQuery";
import { useParams } from "react-router-dom";
import shallow from "zustand/shallow";
import useStore from "@/stores";
import { useTranslation } from "react-i18next";

export type StudentClaimBannerProps = {
	/** Opens the shared Student management modal (provided by RegularClassroom). */
	onViewDetails?: () => void;
};

/**
 * Presents student-claim data from the parent; View Details defers to the parent modal.
 */
export const StudentClaimBanner: React.FC<StudentClaimBannerProps> = ({ onViewDetails }) => {
	const { t } = useTranslation();
	const params = useParams<string>();
	const classroomStore = useStore(
		(state) => ({
			getClassroomV2: state.classroom.getClassroomV2,
		}),
		shallow
	);
	const currentClassStableId = classroomStore.getClassroomV2(params.classId as string)?.stableIdV1;
	const studentClaimsQuery = useStudentClaimsQuery({ classroomStableId: currentClassStableId as string });
	const isClassSessionActiveV2 = useStore((state) => state.classroom.isClassSessionActiveV2(currentClassStableId as string));
	const claimedStudents = isClassSessionActiveV2
		? filterStudentClaimsForOtherClasses(studentClaimsQuery.data ?? [], currentClassStableId)
		: [];
	if (claimedStudents.length === 0) {
		return null;
	}

	return (
		<>
			<Flex
				align="center"
				flexWrap="wrap"
				rowGap={2}
				columnGap={{ base: 2, md: 3 }}
				p={3}
				mb={2}
				width="100%"
				bg="warning.background"
				borderRadius="6px"
				data-testid="student-claims-banner"
			>
				<Box flexShrink={0} lineHeight={0}>
					<ChakraFAIcon icon="fa-triangle-exclamation" variant="solid" color={colours.icon.warning} boxSize={4} aria-hidden />
				</Box>
				<Text flex="1 1 160px" minW={0} fontSize={{ base: "sm", md: "md" }} lineHeight="short">
					{t("classroomDetails.managedByAnotherClass", { count: claimedStudents.length })}
				</Text>
				<Button
					size="sm"
					flexShrink={0}
					backgroundColor={colours.background.warning}
					color={colours.text.body}
					_hover={{ backgroundColor: colours.background.warning, color: colours.text.body }}
					fontWeight={500}
					fontSize="sm"
					borderRadius="md"
					px={3}
					py={1}
					onClick={() => onViewDetails?.()}
					data-testid="student-claims-view-details"
					rightIcon={<ChakraFAIcon icon="fa-arrow-right" variant="solid" boxSize={3} color={colours.text.body} aria-hidden />}
					id="student-claims-view-details-btn"
				>
					{t("classroomDetails.viewDetails")}
				</Button>
			</Flex>
		</>
	);
};
